import {useEffect} from "react";
import {useFormContext} from "react-hook-form";

import {Alert} from "@/components/alert/Alert.tsx";
import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {ServerErrors} from "@/lib/types/common.ts";
import {IntegrationFormData} from "@/lib/types/integrations.ts";
import {SetupInfo} from "@/pages/integrations/edit/subForms/components/SetupInfo.tsx";
import {IntegrationModuleInfo, IntegrationModules} from "@/pages/integrations/integrations.types.ts";

export type FormValuesKey = keyof IntegrationFormData;

interface MicrosoftTeamsFormProps {
  serverErrors?: ServerErrors;
}

export const MicrosoftTeamsForm = ({serverErrors}: MicrosoftTeamsFormProps) => {
  const {setError, watch} = useFormContext();

  useEffect(() => {
    if (serverErrors) {
      Object.keys(serverErrors)
        .filter((k) => k !== "__all__")
        .forEach((key) => setError(`settings.${key}` as FormValuesKey, {message: serverErrors[key][0]}));
    }
  }, [setError, serverErrors]);

  return (
    <>
      <div className="row row-cols-2 mb-3">
        <div className="col-12 mb-3">
          <InputField name="settings.webhook_url" label="Webhook URL" required={true} autoFocus={true} />
        </div>
      </div>
      {(watch("settings.webhook_url") || "").match("https://.*?.webhook.office.com/.*") && (
        <Alert variant={"maintenance"}>
          Microsoft Teams is deprecating the Office 365 connector on October 1st, 2024. To maintain your integration,
          all users must update their Webhook URLs.
        </Alert>
      )}

      <SetupInfo metricsWarning={true}>
        <div className="p-2">In Teams, select 'Workflows' from the Apps menu.</div>
        <div className="p-2">
          Click on 'New Flow' and search the templates for 'Webhook' and select 'Post to a chat/channel when a webhook
          request is received'. Set up the flow to post to your desired destination and copy the webhook URL provided
          and paste it in to the Webhook URL above.
        </div>
        <div className="p-2">
          <a
            target="_blank"
            href="https://support.uptime.com/hc/en-us/articles/360013447340-Configuring-Microsoft-Teams"
          >
            View {IntegrationModuleInfo[IntegrationModules.MS_TEAMS].label} Support Article &rarr;
          </a>
        </div>
      </SetupInfo>
    </>
  );
};
